// frontend/src/hooks/useFleetHealth.js
// Every number the dashboard shows is derived here — labels can never contradict data.

import { useMemo } from 'react';
import { signal } from '../theme/iparc';

export const isExpired = (d) =>
  d ? new Date(d).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) : false;

export const inNextDays = (d, days = 30) =>
  d ? new Date(d) < new Date(Date.now() + days * 864e5) : false;

export const oilRemaining = (v) => {
  const odo = Number(v.odometerReading);
  const next = Number(v.nextOilChangeOdometer);
  if (!Number.isFinite(odo) || !Number.isFinite(next) || next === 0) return null;
  return next - odo;
};

export const oilDue = (v) => {
  const r = oilRemaining(v);
  return r !== null && r <= 500;
};

// Worst condition on a vehicle: 'danger' | 'warn' | 'ok'
export const severity = (v) => {
  if (isExpired(v.insurance?.expiry) || isExpired(v.technicalCheck?.expiry)) return 'danger';
  if (inNextDays(v.insurance?.expiry) || inNextDays(v.technicalCheck?.expiry) || oilDue(v)) return 'warn';
  return 'ok';
};

export const hasIssue = (v) => severity(v) !== 'ok';

export const oilStatus = (v) => {
  const r = oilRemaining(v);
  const interval = Number(v.oilChangeInterval) || 5000;
  if (r === null) return { color: signal.ok, label: '—', pct: '0%' };
  const pct = `${Math.min(100, Math.max(3, ((interval - r) / interval) * 100))}%`;
  if (r <= 0)    return { color: signal.danger, label: `dépassé ${Math.abs(r).toLocaleString('fr-FR')} km`, pct: '100%' };
  if (r <= 500)  return { color: signal.danger, label: `${r.toLocaleString('fr-FR')} km restants`, pct };
  if (r <= 1200) return { color: signal.warn,   label: `${r.toLocaleString('fr-FR')} km restants`, pct };
  return { color: signal.ok, label: `${r.toLocaleString('fr-FR')} km restants`, pct };
};

export const badgesFor = (v) => {
  const out = [];
  if (isExpired(v.insurance?.expiry)) out.push({ text: 'Assurance', color: signal.danger, tint: signal.dangerTint });
  else if (inNextDays(v.insurance?.expiry)) out.push({ text: 'Assur. 30j', color: signal.warn, tint: signal.warnTint });
  if (isExpired(v.technicalCheck?.expiry)) out.push({ text: 'CT', color: signal.danger, tint: signal.dangerTint });
  else if (inNextDays(v.technicalCheck?.expiry)) out.push({ text: 'CT 30j', color: signal.warn, tint: signal.warnTint });
  if (oilDue(v)) out.push({ text: 'Vidange', color: signal.warn, tint: signal.warnTint });
  if (!out.length) out.push({ text: 'Conforme', color: signal.ok, tint: signal.okTint });
  return out;
};

export default function useFleetHealth(vehicles = []) {
  return useMemo(() => {
    const n = vehicles.length;
    const by = (pred) => vehicles.filter(pred);

    const groups = [
      { key: 'insurance', level: 'Bloquant', color: signal.danger, tint: signal.dangerTint,
        title: 'Assurances expirées',
        sub: 'Circulation interdite — amende et immobilisation possibles',
        cta: 'Renouveler les assurances',
        vehicles: by(v => isExpired(v.insurance?.expiry)) },
      { key: 'technical', level: 'Bloquant', color: signal.danger, tint: signal.dangerTint,
        title: 'Contrôles techniques expirés',
        sub: 'Visite à repasser sous 15 jours',
        cta: 'Planifier les visites',
        vehicles: by(v => isExpired(v.technicalCheck?.expiry)) },
      { key: 'oil', level: 'Urgent', color: signal.warn, tint: signal.warnTint,
        title: 'Vidanges dues',
        sub: 'Seuil d’intervalle atteint ou dépassé',
        cta: 'Demander un atelier',
        vehicles: by(oilDue) },
      { key: 'technicalSoon', level: 'À prévoir', color: signal.warn, tint: signal.warnTint,
        title: 'Contrôles techniques bientôt expirés',
        sub: 'Échéance dans moins de 30 jours',
        cta: 'Prendre rendez-vous',
        vehicles: by(v => !isExpired(v.technicalCheck?.expiry) && inNextDays(v.technicalCheck?.expiry)) },
    ].filter(g => g.vehicles.length > 0)
     .map(g => ({ ...g, count: g.vehicles.length }));

    const totalIssues = groups.reduce((t, g) => t + g.count, 0);
    const blocking = groups.filter(g => g.level === 'Bloquant').reduce((t, g) => t + g.count, 0);

    const sevOrder = { ok: 0, warn: 1, danger: 2 };
    const sevColor = { ok: signal.ok, warn: signal.warn, danger: signal.danger };
    const sevs = vehicles.map(severity).sort((a, b) => sevOrder[a] - sevOrder[b]);
    const segments = sevs.map(s => sevColor[s]);
    const count = (s) => sevs.filter(x => x === s).length;

    const insOk = by(v => !isExpired(v.insurance?.expiry)).length;
    const ctOk  = by(v => !isExpired(v.technicalCheck?.expiry)).length;
    const oilOk = by(v => !oilDue(v)).length;
    const docOk = by(v => Boolean(v.insurance?.expiry && v.technicalCheck?.expiry)).length;

    const rowColor = (k) => (n === 0 ? signal.ok : k === n ? signal.ok : k / n < 0.5 ? signal.danger : signal.warn);
    const pct = (k) => (n === 0 ? '0%' : `${Math.round((k / n) * 100)}%`);

    const breakdown = [
      { label: 'Assurance',      num: `${insOk}/${n}`, pct: pct(insOk), color: rowColor(insOk) },
      { label: 'Contrôle tech.', num: `${ctOk}/${n}`,  pct: pct(ctOk),  color: rowColor(ctOk) },
      { label: 'Entretien',      num: `${oilOk}/${n}`, pct: pct(oilOk), color: rowColor(oilOk) },
      { label: 'Documents',      num: `${docOk}/${n}`, pct: pct(docOk), color: rowColor(docOk) },
    ];

    const score  = n ? Math.round(((insOk + ctOk + oilOk + docOk) / (4 * n)) * 100) : 100;
    const uplift = n ? Math.round(((n + ctOk + oilOk + docOk) / (4 * n)) * 100) : 100;

    return {
      count: n, groups, totalIssues, blocking,
      segments,
      splitLabel: `${count('ok')} conformes · ${count('warn')} à surveiller · ${count('danger')} en défaut`,
      triageSub: `${totalIssues} échéance${totalIssues > 1 ? 's' : ''} sur ${n} véhicule${n > 1 ? 's' : ''} — ${blocking} immobilise${blocking > 1 ? 'nt' : ''} le véhicule`,
      breakdown, score, uplift,
      insuranceGap: n - insOk,
      scoreColor: score < 50 ? signal.danger : score < 80 ? signal.warn : signal.ok,
      scoreLabel: score < 50 ? 'Critique' : score < 80 ? 'À redresser' : 'Conforme',
      issueCount: vehicles.filter(hasIssue).length,
      okCount: n - vehicles.filter(hasIssue).length,
    };
  }, [vehicles]);
}
